// Pure windowing math for the virtualized review table. Kept framework-free so
// it can be unit-tested without a DOM. Row heights are variable (issue notes and
// change lists wrap), so callers pass a per-row height array — measured where
// known, estimated otherwise — rather than assuming a fixed row height.

export interface VisibleRange {
  start: number
  end: number
  offsetTop: number
  totalHeight: number
}

// Cumulative pixel offset of the top edge of each row. Length is heights.length + 1
// so offsets[i] is the top of row i and offsets[length] is the total content height.
export const buildRowOffsets = (heights: number[]): number[] => {
  const offsets = new Array(heights.length + 1)
  offsets[0] = 0
  for (let index = 0; index < heights.length; index++) {
    const height = heights[index]
    offsets[index + 1] = offsets[index] + (height > 0 ? height : 0)
  }
  return offsets
}

// Largest row index whose top offset is <= scrollTop (binary search over the
// monotonic offsets array). Returns 0 when scrollTop is above the first row.
const findFirstVisibleIndex = (offsets: number[], scrollTop: number): number => {
  let low = 0
  let high = offsets.length - 1
  while (low < high) {
    const mid = (low + high + 1) >> 1
    if (offsets[mid] <= scrollTop) {
      low = mid
    } else {
      high = mid - 1
    }
  }
  return low
}

// Given per-row heights and the current scroll position, return the slice of rows
// that should be mounted (plus overscan) and the translateY offset for that slice.
export const computeVisibleRange = (params: {
  heights: number[]
  scrollTop: number
  viewportHeight: number
  overscan?: number
}): VisibleRange => {
  const rowCount = params.heights.length
  const offsets = buildRowOffsets(params.heights)
  const totalHeight = offsets[rowCount]

  if (rowCount === 0) {
    return { start: 0, end: 0, offsetTop: 0, totalHeight: 0 }
  }

  const overscan = Math.max(0, Math.floor(params.overscan || 0))
  const scrollTop = Math.min(Math.max(0, params.scrollTop || 0), totalHeight)
  const viewportHeight = Math.max(0, params.viewportHeight || 0)
  const viewportBottom = scrollTop + viewportHeight

  let start = findFirstVisibleIndex(offsets, scrollTop)
  let end = start
  while (end < rowCount && offsets[end] < viewportBottom) {
    end++
  }
  // Always keep at least one row mounted so measurement can proceed.
  if (end === start) {
    end = Math.min(rowCount, start + 1)
  }

  start = Math.max(0, start - overscan)
  end = Math.min(rowCount, end + overscan)

  return { start, end, offsetTop: offsets[start], totalHeight }
}
